<!--
  Created 22.06.2023
  Simple small modal with buttons and dismiss icon.
  Originated from Tailwind UI component: Overlays, Modals, SimpleWithDismissButton:
  https://tailwindui.com/components/application-ui/overlays/modals#component-6a0b582f00c5ec38bf748d9a75559f04
-->
<template>
  <TransitionRoot
    as="template"
    :show="showModal">
    <Dialog
      as="div"
      class="relative z-10"
      @close="showModal = false">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 md:max-w-xl"
              :class="{'overflow-hidden': overflowHidden}">
              <div class="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                <button
                  type="button"
                  class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  @click="showModal = false">
                  <span class="sr-only">Close</span>
                  <XMarkIcon
                    class="h-6 w-6"
                    aria-hidden="true" />
                </button>
              </div>
              <div class="sm:flex sm:items-start">
                <div class="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                  <SquaresPlusIcon
                    class="h-6 w-6 text-blue-600"
                    aria-hidden="true" />
                </div>
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle
                    as="h3"
                    class="text-base font-semibold leading-6 text-gray-900">
                    {{ heading }}
                  </DialogTitle>
                  <div class="mt-2">
                    <p
                      v-if="text"
                      class="text-sm text-gray-500">
                      {{ text }}
                    </p>
                    <slot v-else />
                  </div>
                </div>
              </div>
              <div
                v-if="buttonText || $slots.buttons"
                class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  v-if="buttonText"
                  tabindex="1"
                  type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2
                  text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  @click="showModal = false">
                  {{ buttonText || 'Close' }}
                </button>
                <slot
                  v-else
                  name="buttons" />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import {
  computed, ref, watch,
} from 'vue';
import {
  Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot,
} from '@headlessui/vue';
import {
  XMarkIcon, SquaresPlusIcon,
} from '@heroicons/vue/24/outline';

const emit = defineEmits(['update:show']);

const props = defineProps({
  show: {
    type: Boolean,
    required: false,
    default: false,
  },
  heading: {
    type: String,
    default: '',
    required: false,
  },
  text: {
    type: String,
    default: '',
    required: false,
  },
  buttonText: {
    type: String,
    default: '',
    required: false,
  },
  overflowHidden: {
    type: Boolean,
    default: true,
    required: false,
  },
});

const showModal = computed({
  get() {
    return props.show;
  },
  set(value) {
    emit('update:show', value);
  },
});

watch(() => showModal.value, (value) => {
  if (value === true) {
    document.body.classList.add('modal-open');
  } else {
    document.body.classList.remove('modal-open');
  }
});

</script>
